import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { AIChatWidget } from './components/AIChatWidget';
import { SponsorBanner } from './components/SponsorBanner';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Dashboard from './pages/Dashboard';
import VerifyCertificate from './pages/VerifyCertificate';
import VerifyExternal from './pages/VerifyExternal';
import About from './pages/About';
import Contact from './pages/Contact';
import Delegates from './pages/Delegates';
import Sponsors from './pages/Sponsors';
import ManageCourses from './pages/admin/ManageCourses';
import ManageCertificates from './pages/admin/ManageCertificates';
import MediaLibrary from './pages/admin/MediaLibrary';
import CertificateTemplates from './pages/admin/CertificateTemplates';
import CertificateTemplateEditor from './pages/admin/CertificateTemplateEditor';
import ManageDelegates from './pages/admin/ManageDelegates';
import AIKnowledgeManager from './pages/admin/AIKnowledgeManager';
import ManageUsers from './pages/admin/ManageUsers';
import UploadExternalCertificate from './pages/admin/UploadExternalCertificate';
import EditCourseContent from './pages/admin/EditCourseContent';
import ManageCMS from './pages/admin/ManageCMS';
import AdminSettings from './pages/admin/AdminSettings';
import AdminHelp from './pages/admin/AdminHelp';
import CourseLearn from './pages/CourseLearn';
import EnrollForm from './pages/EnrollForm';
import Articles from './pages/Articles';
import ArticleDetail from './pages/ArticleDetail';
import ManageArticles from './pages/admin/ManageArticles';
import ManageEnrollmentRequests from './pages/admin/ManageEnrollmentRequests';
import ManageCountryContacts from './pages/admin/ManageCountryContacts';
import Profile from './pages/Profile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Scholarships from './pages/Scholarships';
import PageBuilder from './pages/admin/PageBuilder';

import './App.css';

function AppContent() {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Full-screen pages that hide the nav/footer
  const isFullScreen = location.pathname.includes('/learn');

  return (
    <div className="min-h-screen flex flex-col">
      {!isFullScreen && <Navbar user={user} onLogout={logout} />}
      {!isFullScreen && <SponsorBanner />}
      <main className={isFullScreen ? 'flex-1 flex flex-col overflow-hidden h-screen' : 'flex-1'}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/becas" element={<Scholarships />} />
          <Route path="/scholarships" element={<Scholarships />} />
          <Route path="/courses/:id" element={<CourseDetail />} />
          <Route path="/courses/:id/learn" element={<CourseLearn />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/verify" element={<VerifyCertificate />} />
          <Route path="/verificar-certificado" element={<VerifyCertificate />} />
          <Route path="/verify-external/:code" element={<VerifyExternal />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/delegates" element={<Delegates />} />
          <Route path="/sponsors" element={<Sponsors />} />
          <Route path="/admin/courses" element={<ManageCourses />} />
          <Route path="/admin/certificates" element={<ManageCertificates />} />
          <Route path="/admin/media" element={<MediaLibrary />} />
          <Route path="/admin/certificate-templates" element={<CertificateTemplates />} />
          <Route path="/admin/certificate-templates/new" element={<CertificateTemplateEditor />} />
          <Route path="/admin/certificate-templates/:templateId" element={<CertificateTemplateEditor />} />
          <Route path="/admin/delegates" element={<ManageDelegates />} />
          <Route path="/admin/ai-knowledge" element={<AIKnowledgeManager />} />
          <Route path="/admin/users" element={<ManageUsers />} />
          <Route path="/admin/certificates/upload-external" element={<UploadExternalCertificate />} />
          <Route path="/admin/certificates/upload-external/edit/:certId" element={<UploadExternalCertificate />} />
          <Route path="/admin/courses/:courseId/content" element={<EditCourseContent />} />
          <Route path="/admin/cms" element={<ManageCMS />} />
          <Route path="/admin/pages/:slug" element={<PageBuilder />} />
          <Route path="/admin/pages" element={<PageBuilder />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/help" element={<AdminHelp />} />
          <Route path="/admin/articles" element={<ManageArticles />} />
          <Route path="/admin/enrollment-requests" element={<ManageEnrollmentRequests />} />
          <Route path="/admin/country-contacts" element={<ManageCountryContacts />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/politica-de-datos" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/articulos" element={<Articles />} />
          <Route path="/articulos/:slug" element={<ArticleDetail />} />
          <Route path="/enroll/:courseId" element={<EnrollForm />} />
        </Routes>
      </main>
      {!isFullScreen && <Footer />}
      <Toaster position="top-right" />
      {!isFullScreen && <AIChatWidget isLoggedIn={!!user} />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
